/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';

import {
  getRegistrosAltaIMSS,
  postRegistroAltaIMSS,
  patchRegistroAltaIMSS,
  deleteRegistroAltaIMSS,
  getRegistroAltaIMSS,
  autorizarRegistroAltaIMSS,
} from 'api/catalogos/registrosAltaIMSS';

const RegistroAltaIMSS = ({
  incidenciaEmpleadoId, isBlocked, permission, onAfterAutorizar,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});

  const formInstances = [form];
  const DATE_FORMAT = 'DD/MM/YYYY';

  const normalizeEstados = (items) => items.map((item) => ({
    ...item,
    estados_globales: item.estados_globales?.id ?? item.estados_globales,
  }));

  const fetchData = async () => {
    if (!incidenciaEmpleadoId) return;
    setLoading(true);
    const _data = await getRegistrosAltaIMSS({ incidencia_empleado_id: incidenciaEmpleadoId });
    setData(formatReceived(normalizeEstados(_data)));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getRegistrosAltaIMSS({ incidencia_empleado_id: incidenciaEmpleadoId }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(normalizeEstados(_data)));
        setLoading(false);
      }
    };

    if (incidenciaEmpleadoId) {
      fetchAll();
    } else {
      setData([]);
    }

    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [incidenciaEmpleadoId]);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _registro = await getRegistroAltaIMSS(match.id);

    if (_registro) {
      form.resetFields();
      form.setFieldsValue({
        ...formatReceived(_registro),
        estados_globales: _registro.estados_globales?.id ?? _registro.estados_globales,
        fecha_de_registro: _registro.fecha_de_registro
          ? moment(_registro.fecha_de_registro) : null,
        fecha_de_alta_imss: _registro.fecha_de_alta_imss
          ? moment(_registro.fecha_de_alta_imss) : null,
      });
      setVisible(true);
    }
    setLoading(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(true),
        incidencia_empleado: incidenciaEmpleadoId, // FK hacia el padre
        fecha_de_registro: form.getFieldValue('fecha_de_registro')?.format('YYYY-MM-DD'),
        fecha_de_alta_imss: form.getFieldValue('fecha_de_alta_imss')?.format('YYYY-MM-DD'),
      };

      const [key] = selectedRowKeys;
      const saved = await (key ? patchRegistroAltaIMSS(key, values) : postRegistroAltaIMSS(values));

      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteRegistroAltaIMSS(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    // El registro nace Activo por defecto
    form.setFieldsValue({
      estados_globales: ESTATUS_ACTIVO,
    });
    setVisible(true);
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;

      if (!key) {
        setLoading(false);
        return; // Retorno vacío para salir de la función
      }

      const saved = await autorizarRegistroAltaIMSS(key);
      if (saved) {
        await fetchData();
        onSuccess({}, 'Registro autorizado correctamente');
        onCancel();
        if (onAfterAutorizar) onAfterAutorizar();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Fecha de Registro',
      dataIndex: 'fecha_de_registro',
      key: 'fecha_de_registro',
      width: 200,
      render: (val) => (val ? moment(val).format(DATE_FORMAT) : ''),
    },
    {
      titleText: 'Fecha de Alta IMSS',
      dataIndex: 'fecha_de_alta_imss',
      key: 'fecha_de_alta_imss',
      width: 200,
      render: (val) => (val ? moment(val).format(DATE_FORMAT) : ''),
    },
    {
      titleText: 'No. de Documento',
      dataIndex: 'no_de_documento',
      key: 'no_de_documento',
      width: 250,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const rules = {
    required: [{ required: true, message: 'El campo es requerido' }],
  };

  const disableEditDelete = selectedData?.estados_globales === ESTATUS_AUTORIZADO || isBlocked;

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd: isBlocked ? undefined : onClickAdd,
            onClickEdit: isBlocked ? undefined : () => onClickEdit(),
            onClickDelete: isBlocked ? undefined : () => setVisibleAlert(true),
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'registroaltaimss', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO || isBlocked,
              className: 'success',
            },
          ]}
          disableDelete={disableEditDelete}
          disableEdit={disableEditDelete}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Registro de Alta IMSS`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableEditDelete}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="fecha_de_registro"
                  label="Fecha de Registro"
                  rules={rules.required}
                >
                  <DatePicker
                    format={DATE_FORMAT}
                    style={{ width: '100%' }}
                    disabled={disableEditDelete}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="fecha_de_alta_imss"
                  label="Fecha de Alta IMSS"
                  rules={rules.required}
                >
                  <DatePicker
                    format={DATE_FORMAT}
                    style={{ width: '100%' }}
                    disabled={disableEditDelete}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="no_de_documento"
                  label="No. de Documento"
                  rules={rules.required}
                >
                  <Input disabled={disableEditDelete} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.required}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estados}
                    disabled // Siempre bloqueado según el requerimiento visual
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="Registro de Alta IMSS"
          loading={loading}
        />

        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle="Autorizar Registro de Alta IMSS"
          open={visibleAutorizar}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default RegistroAltaIMSS;
